import { useState, useEffect } from "react";
import { NavLink, Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowRightLeft,
  BarChart2,
  Megaphone,
  PackageSearch,
  Sparkles,
  Gauge,
  Radar,
  Radio,
  Upload,
  Store,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Telescope,
  Sun,
  Moon,
} from "lucide-react";

import { UploadPage } from "@/features/upload/UploadPage";
import { ProductsPage } from "@/features/products/ProductsPage";
import { CurationPage } from "@/features/curation/CurationPage";
import { PricingPage } from "@/features/pricing/PricingPage";
import { CompetitorsPage } from "@/features/competitors/CompetitorsPage";
import { StorePage } from "@/features/store/StorePage";
import { ShopifyCatalogPage } from "@/features/store/ShopifyCatalogPage";
import { DiscoveryPage } from "@/features/discovery/DiscoveryPage";
import { AnalyticsPage } from "@/features/analytics/AnalyticsPage";
import { MarketingPage } from "@/features/marketing/MarketingPage";
import { SocialPage } from "@/features/social/SocialPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { EnvBanner } from "@/components/EnvBanner";
import { cn } from "@/lib/utils";
import { Toaster } from "sonner";

const nav = [
  { to: "/upload", label: "Upload", icon: Upload },
  { to: "/products", label: "Catalog", icon: PackageSearch },
  { to: "/curate", label: "Curate", icon: Sparkles },
  { to: "/discovery", label: "Discovery", icon: Telescope },
  { to: "/analytics", label: "Analytics", icon: BarChart2 },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/social", label: "Social", icon: Radio },
  { to: "/pricing", label: "Pricing", icon: Gauge },
  { to: "/competitors", label: "Competitors", icon: Radar },
  { to: "/store", label: "Store", icon: Store },
];

const SIDEBAR_KEY = "symphony:sidebar-collapsed";
const THEME_KEY = "symphony:theme";

function LoginCallback() {
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate("/", { replace: true });
  }, [isAuthenticated, isLoading, navigate]);
  return (
    <div className="flex items-center justify-center min-h-screen bg-background text-muted-foreground text-sm">
      {isLoading ? "Completing sign-in…" : "Redirecting…"}
    </div>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  if (import.meta.env.VITE_AUTH_BYPASS === "true") return <>{children}</>;
  if (isLoading) return (
    <div className="flex items-center justify-center min-h-screen bg-background text-muted-foreground text-sm">
      Authenticating…
    </div>
  );
  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }
  return <>{children}</>;
}

export function App() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [isDark, setIsDark] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(THEME_KEY);
      return stored ? stored === "dark" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, String(collapsed));
    } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, isDark ? "dark" : "light");
      document.documentElement.classList.toggle("dark", isDark);
    } catch { /* ignore */ }
  }, [isDark]);

  return (
    <AuthGuard>
    <>
    <Toaster position="bottom-right" richColors closeButton />
    <div
      className={cn(
        "min-h-screen grid transition-[grid-template-columns] duration-200",
        collapsed ? "grid-cols-[56px_1fr]" : "grid-cols-[240px_1fr]",
      )}
    >
      {/* ── Sidebar ── */}
      <aside className="glass border-r border-border/60 p-3 flex flex-col gap-4 sticky top-0 h-screen overflow-hidden">
        {/* Logo row + collapse toggle */}
        <div className="flex items-center justify-between gap-2 min-h-[36px]">
          {!collapsed && (
            <div className="flex items-center gap-2 px-1 overflow-hidden">
              <ArrowRightLeft className="h-5 w-5 text-ember shrink-0" />
              <div className="leading-tight overflow-hidden">
                <div className="font-semibold tracking-tight truncate">Symphony</div>
                <div className="text-xs text-muted-foreground truncate">OnCloudTech control</div>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="flex justify-center w-full">
              <ArrowRightLeft className="h-5 w-5 text-ember" />
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={cn(
              "shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors",
              collapsed && "mx-auto",
            )}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed
              ? <PanelLeftOpen className="h-4 w-4" />
              : <PanelLeftClose className="h-4 w-4" />
            }
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex flex-col gap-0.5 flex-1">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md text-sm transition-colors",
                  collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive && "bg-accent text-accent-foreground font-medium",
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="flex flex-col gap-0.5 border-t border-border/60 pt-3">
          <NavLink
            to="/settings"
            title={collapsed ? "Settings" : undefined}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md text-sm transition-colors",
                collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2",
                "hover:bg-accent hover:text-accent-foreground",
                isActive && "bg-accent text-accent-foreground font-medium",
              )
            }
          >
            <Settings className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">Settings</span>}
          </NavLink>

          {/* Theme toggle */}
          <button
            onClick={() => setIsDark((d) => !d)}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className={cn(
              "flex items-center gap-3 rounded-md text-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-accent",
              collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2",
            )}
          >
            {isDark
              ? <Sun className="h-4 w-4 shrink-0" />
              : <Moon className="h-4 w-4 shrink-0" />
            }
            {!collapsed && <span className="truncate">{isDark ? "Light mode" : "Dark mode"}</span>}
          </button>

          {!collapsed && (
            <div className="text-[11px] text-muted-foreground px-2 leading-relaxed mt-1">
              <div>TD SYNNEX → Shopify</div>
              <div>Dell · Lenovo · Samsung · Anker</div>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex flex-col min-w-0">
        <EnvBanner />
        <div className="flex-1 p-8 min-w-0">
          <Routes>
            <Route path="/login" element={<LoginCallback />} />
            <Route path="/" element={<Navigate to="/upload" replace />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/curate" element={<CurationPage />} />
            <Route path="/curate/:sku" element={<CurationPage />} />
            <Route path="/curated/:id" element={<CurationPage />} />
            <Route path="/discovery" element={<DiscoveryPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/marketing" element={<MarketingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/competitors" element={<CompetitorsPage />} />
            <Route path="/social" element={<SocialPage />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/store/shopify-catalog" element={<ShopifyCatalogPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </div>
      </main>
    </div>
    </>
    </AuthGuard>
  );
}
